import type { EnvironmentId } from "@t3tools/contracts";
import { createStore, type StoreApi } from "zustand/vanilla";

import { randomUUID } from "../lib/utils";
import {
  toVoiceTranscriptionFailure,
  type VoiceTranscriptionFailure,
  type VoiceTranscriptionRequest,
} from "../voiceTranscription";
import type {
  VoiceClipCoordination,
  VoiceClipLockRelease,
  VoiceClipPersistence,
  VoiceClipRecord,
} from "./voiceClipPersistence";

export const VOICE_CLIP_CHUNK_MS = 1000;

export interface VoiceClip extends VoiceClipRecord {
  readonly persisted: boolean;
  readonly local: boolean;
}

export interface VoiceClipStoreState {
  readonly clips: Readonly<Record<string, VoiceClip>>;
}

export type VoiceClipTranscriber = (request: VoiceTranscriptionRequest) => Promise<string>;

export interface CapturedVoiceRecording {
  readonly blob: Blob;
  readonly mimeType: string;
  readonly durationMs: number;
}

export interface VoiceClipController {
  readonly store: StoreApi<VoiceClipStoreState>;
  readonly ensureHydrated: () => Promise<void>;
  readonly beginRecording: (input: {
    readonly targetKey: string;
    readonly environmentId: EnvironmentId;
    readonly mimeType: string;
  }) => string;
  readonly appendChunk: (id: string, chunk: Blob) => void;
  readonly finishRecording: (
    id: string,
    recording: CapturedVoiceRecording,
    options?: { readonly failure?: VoiceTranscriptionFailure },
  ) => Promise<void>;
  readonly finishRecordingFromChunks: (id: string) => Promise<void>;
  readonly transcribe: (id: string) => Promise<void>;
  readonly discard: (id: string) => Promise<void>;
  readonly consumeTranscript: (
    id: string,
    insert: (transcript: string) => boolean,
  ) => Promise<boolean>;
  readonly readAudio: (id: string) => Promise<Blob | undefined>;
}

const isInFlight = (status: VoiceClipRecord["status"]) =>
  status === "recording" || status === "transcribing";

function toRecord(clip: VoiceClip): VoiceClipRecord {
  return {
    id: clip.id,
    targetKey: clip.targetKey,
    environmentId: clip.environmentId,
    createdAt: clip.createdAt,
    mimeType: clip.mimeType,
    durationMs: clip.durationMs,
    sizeBytes: clip.sizeBytes,
    status: clip.status,
    failure: clip.failure,
    transcript: clip.transcript,
  };
}

export function createVoiceClipController(deps: {
  readonly persistence: VoiceClipPersistence;
  readonly coordination: VoiceClipCoordination;
  readonly transcriber: VoiceClipTranscriber;
  readonly now?: () => number;
  readonly createId?: () => string;
}): VoiceClipController {
  const { persistence, coordination } = deps;
  const now = deps.now ?? Date.now;
  const createId = deps.createId ?? randomUUID;
  const store = createStore<VoiceClipStoreState>(() => ({ clips: {} }));
  const audio = new Map<string, Blob>();
  const chunks = new Map<string, Blob[]>();
  const holds = new Map<string, Promise<VoiceClipLockRelease | null>>();
  const transcriptions = new Map<string, AbortController>();
  const consuming = new Set<string>();
  const watched = new Set<string>();
  const discarded = new Set<string>();
  const { transcriber } = deps;
  let hydration: Promise<void> | null = null;

  const readClip = (id: string) => store.getState().clips[id];

  const writeClip = (clip: VoiceClip) => {
    if (discarded.has(clip.id)) return;
    store.setState((state) => ({ clips: { ...state.clips, [clip.id]: clip } }));
  };

  const dropClip = (id: string) => {
    store.setState((state) => {
      if (!(id in state.clips)) return state;
      const clips = { ...state.clips };
      delete clips[id];
      return { clips };
    });
  };

  const persistRecord = async (clip: VoiceClip): Promise<VoiceClip> => {
    if (discarded.has(clip.id)) return clip;
    try {
      await persistence.saveRecord(toRecord(clip));
      coordination.publish();
      return clip;
    } catch {
      return { ...clip, persisted: false };
    }
  };

  const save = async (clip: VoiceClip) => {
    writeClip(clip);
    const stored = await persistRecord(clip);
    if (stored !== clip && readClip(clip.id) === clip) writeClip(stored);
  };

  const hold = async (id: string): Promise<boolean> => {
    const existing = holds.get(id);
    if (existing) return (await existing) !== null;
    const pending = coordination.hold(id);
    holds.set(id, pending);
    if (await pending) return true;
    holds.delete(id);
    return false;
  };

  const release = async (id: string) => {
    const pending = holds.get(id);
    holds.delete(id);
    const unlock = await pending;
    await unlock?.();
  };

  const recoverInterrupted = async (record: VoiceClipRecord): Promise<VoiceClipRecord> => {
    if (record.status === "recording") {
      const parts = await persistence.loadChunks(record.id);
      const blob = new Blob(parts, { type: record.mimeType });
      await persistence.saveAudio(record.id, blob);
      return {
        ...record,
        status: "failed",
        failure: { kind: "interrupted", phase: "recording" },
        durationMs: parts.length * VOICE_CLIP_CHUNK_MS,
        sizeBytes: blob.size,
      };
    }
    return { ...record, status: "failed", failure: { kind: "interrupted", phase: "transcribing" } };
  };

  const watch = (id: string) => {
    if (watched.has(id) || holds.has(id)) return;
    watched.add(id);
    void coordination
      .withLock(id, async () => {
        if (holds.has(id) || discarded.has(id)) return;
        const record = await persistence.loadRecord(id);
        if (!record) {
          dropClip(id);
          return;
        }
        const settled = isInFlight(record.status) ? await recoverInterrupted(record) : record;
        if (settled !== record) {
          await persistence.saveRecord(settled);
          coordination.publish();
        }
        writeClip({ ...settled, persisted: true, local: false });
      })
      .catch(() => undefined)
      .finally(() => watched.delete(id));
  };

  const refresh = async () => {
    let records: VoiceClipRecord[];
    try {
      records = await persistence.loadRecords();
    } catch {
      return;
    }
    store.setState((state) => {
      const clips: Record<string, VoiceClip> = {};
      for (const [id, clip] of Object.entries(state.clips)) {
        if (holds.has(id) || !clip.persisted) clips[id] = clip;
      }
      for (const record of records) {
        if (holds.has(record.id) || discarded.has(record.id)) continue;
        clips[record.id] = { ...record, persisted: true, local: false };
      }
      return { clips };
    });
    for (const record of records) {
      if (isInFlight(record.status) && !discarded.has(record.id)) watch(record.id);
    }
  };

  const loadAudio = async (id: string): Promise<Blob | undefined> => {
    const cached = audio.get(id);
    if (cached) return cached;
    const stored = await persistence.loadAudio(id).catch(() => undefined);
    if (stored) audio.set(id, stored);
    return stored;
  };

  const runTranscriber = async (
    clip: VoiceClip,
    signal: AbortSignal,
  ): Promise<Pick<VoiceClip, "status" | "failure" | "transcript">> => {
    const blob = await loadAudio(clip.id);
    if (!blob) {
      return { status: "failed", failure: { kind: "audioUnavailable" }, transcript: null };
    }
    try {
      const transcript = await transcriber({
        environmentId: clip.environmentId,
        audio: blob,
        mimeType: clip.mimeType,
        signal,
      });
      return { status: "transcribed", failure: null, transcript };
    } catch (error) {
      return { status: "failed", failure: toVoiceTranscriptionFailure(error), transcript: null };
    }
  };

  const transcribe = async (id: string) => {
    const initial = readClip(id);
    if (!initial || transcriptions.has(id) || discarded.has(id)) return;
    if (initial.status !== "pending" && initial.status !== "failed") return;
    const controller = new AbortController();
    transcriptions.set(id, controller);
    const claimed: VoiceClip = { ...initial, status: "transcribing", failure: null, local: true };
    writeClip(claimed);
    try {
      if (!(await hold(id))) {
        if (readClip(id) === claimed) writeClip(initial);
        return;
      }
      const current = readClip(id);
      if (!current || controller.signal.aborted) return;
      const started: VoiceClip = { ...current, status: "transcribing", failure: null, local: true };
      await save(started);
      const outcome = await runTranscriber(started, controller.signal);
      const latest = readClip(id);
      if (!latest || controller.signal.aborted) return;
      const stored = latest.persisted
        ? await persistence.loadRecord(id).catch(() => toRecord(latest))
        : toRecord(latest);
      if (!stored) {
        dropClip(id);
        return;
      }
      const settled = await persistRecord({ ...latest, ...outcome });
      await release(id);
      writeClip(settled);
    } finally {
      if (transcriptions.get(id) === controller) transcriptions.delete(id);
      if (holds.has(id)) await release(id);
    }
  };

  const finishRecording: VoiceClipController["finishRecording"] = async (
    id,
    recording,
    options,
  ) => {
    const clip = readClip(id);
    if (!clip) return;
    chunks.delete(id);
    audio.set(id, recording.blob);
    let persisted = clip.persisted;
    try {
      await persistence.saveAudio(id, recording.blob);
    } catch {
      persisted = false;
    }
    if (discarded.has(id)) {
      await persistence.remove(id).catch(() => undefined);
      return;
    }
    const captured = {
      ...(readClip(id) ?? clip),
      mimeType: recording.mimeType,
      durationMs: recording.durationMs,
      sizeBytes: recording.blob.size,
      persisted,
    };
    if (options?.failure) {
      const failed = await persistRecord({
        ...captured,
        status: "failed",
        failure: options.failure,
        transcript: null,
      });
      await release(id);
      writeClip(failed);
      return;
    }
    writeClip(await persistRecord({ ...captured, status: "pending" }));
    await transcribe(id);
  };

  const discard = async (id: string) => {
    discarded.add(id);
    transcriptions.get(id)?.abort();
    transcriptions.delete(id);
    audio.delete(id);
    chunks.delete(id);
    dropClip(id);
    await persistence.remove(id).catch(() => undefined);
    await release(id);
    coordination.publish();
  };

  const consumeTranscript: VoiceClipController["consumeTranscript"] = async (id, insert) => {
    const initial = readClip(id);
    if (!initial || initial.status !== "transcribed" || consuming.has(id)) return false;
    consuming.add(id);
    try {
      return await coordination.withLock(id, async () => {
        const current = readClip(id);
        if (!current) return false;
        const record = current.persisted
          ? await persistence.loadRecord(id).catch(() => toRecord(current))
          : toRecord(current);
        if (!record) {
          dropClip(id);
          return false;
        }
        if (record.status !== "transcribed" || record.transcript === null) return false;
        if (!insert(record.transcript)) return false;
        discarded.add(id);
        audio.delete(id);
        dropClip(id);
        await persistence.remove(id).catch(() => undefined);
        coordination.publish();
        return true;
      });
    } finally {
      consuming.delete(id);
    }
  };

  return {
    store,
    ensureHydrated: () => {
      hydration ??= (async () => {
        coordination.subscribe(() => void refresh());
        await refresh();
      })();
      return hydration;
    },
    beginRecording: ({ targetKey, environmentId, mimeType }) => {
      const id = createId();
      holds.set(id, coordination.hold(id));
      chunks.set(id, []);
      void save({
        id,
        targetKey,
        environmentId,
        createdAt: now(),
        mimeType,
        durationMs: 0,
        sizeBytes: 0,
        status: "recording",
        failure: null,
        transcript: null,
        persisted: true,
        local: true,
      });
      return id;
    },
    appendChunk: (id, chunk) => {
      const list = chunks.get(id);
      const clip = readClip(id);
      if (!list || !clip) return;
      const index = list.length;
      list.push(chunk);
      writeClip({
        ...clip,
        sizeBytes: clip.sizeBytes + chunk.size,
        durationMs: list.length * VOICE_CLIP_CHUNK_MS,
      });
      void persistence.appendChunk(id, index, chunk).catch(() => {
        const latest = readClip(id);
        if (latest?.persisted) writeClip({ ...latest, persisted: false });
      });
    },
    finishRecording,
    finishRecordingFromChunks: async (id) => {
      const clip = readClip(id);
      const list = chunks.get(id);
      if (!clip || !list) return;
      await finishRecording(id, {
        blob: new Blob(list, { type: clip.mimeType }),
        mimeType: clip.mimeType,
        durationMs: list.length * VOICE_CLIP_CHUNK_MS,
      });
    },
    transcribe,
    discard,
    consumeTranscript,
    readAudio: loadAudio,
  };
}
